import type { SiteDto } from '@pl/domain';
import { useCallback, useState } from 'react';
import { useMe } from '../lib/queries';

/** Per user, so a shared floor phone does not hand the next person the previous person's site. */
const keyFor = (userId: string) => `pl.scanSite.${userId}`;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // private mode, or storage blocked
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* the choice just does not survive a reload */
  }
}

/**
 * The site every scan on this phone belongs to. It is read straight through on each render so
 * the header never flashes the wrong site on the way in; the stored id is ignored when it is
 * not one of the sites this user may work at (moved, or a site retired), falling back to their
 * first one.
 */
export function useScanSite(): { site: SiteDto | undefined; sites: SiteDto[]; setSiteId(id: string): void } {
  const me = useMe();
  const sites = me.data?.sites ?? [];
  const userId = me.data?.user.id;
  // Bumped on a write, because the value lives in localStorage rather than in React state.
  const [, setWrites] = useState(0);

  const stored = userId ? read(keyFor(userId)) : null;
  const site = sites.find((s) => s.id === stored) ?? sites[0];

  const setSiteId = useCallback(
    (id: string) => {
      if (userId) write(keyFor(userId), id);
      setWrites((n) => n + 1);
    },
    [userId],
  );

  return { site, sites, setSiteId };
}
